// ============================================================
//  src/pages/CollaborationsPage.js
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  listCollabs, listReceivedInvites, inviteCollaborator,
  respondToInvite, removeCollaborator, updateCollabAccess,
  listCollabBots, listBots, getBillingOverview,
} from '../api/client';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// ── Permission definitions ────────────────────────────────────
const ALL_PERMS = [
  { key: 'commands', icon: '⌨️', label: 'Commands', desc: 'View & edit bot commands' },
  { key: 'panel', icon: '🗂️', label: 'Panel', desc: 'View & edit panel config' },
  { key: 'flows', icon: '🌊', label: 'Flows', desc: 'Build & publish flows' },
  { key: 'users', icon: '👥', label: 'Users', desc: 'View bot users' },
  { key: 'logs', icon: '📊', label: 'Logs', desc: 'View runtime logs' },
  { key: 'endpoints', icon: '🔌', label: 'Endpoints', desc: 'Create & run custom code endpoints' },
  { key: 'settings', icon: '⚙️', label: 'Settings', desc: 'Edit bot settings & credentials' },
];

const FULL_ACCESS = ALL_PERMS.map(p => p.key);

const ACCESS_PRESETS = [
  {
    id: 'full',
    label: 'Full Access',
    desc: 'Can do everything the owner can do',
    color: 'var(--danger)',
    bg: 'rgba(255,59,48,0.08)',
    perms: FULL_ACCESS,
  },
  {
    id: 'editor',
    label: 'Editor',
    desc: 'Commands, Panel, Flows — no Settings',
    color: 'var(--primary)',
    bg: 'var(--primary-light)',
    perms: ['commands', 'panel', 'flows', 'users', 'logs'],
  },
  {
    id: 'viewer',
    label: 'Viewer',
    desc: 'Read-only — Users and Logs only',
    color: 'var(--success)',
    bg: 'rgba(0,196,140,0.08)',
    perms: ['users', 'logs'],
  },
  {
    id: 'custom',
    label: 'Custom',
    desc: 'Choose specific permissions',
    color: 'var(--warning)',
    bg: 'rgba(255,149,0,0.08)',
    perms: null,
  },
];

function presetFromPerms(perms = []) {
  const sorted = [...perms].sort().join(',');
  for (const p of ACCESS_PRESETS) {
    if (p.perms && [...p.perms].sort().join(',') === sorted) return p.id;
  }
  return 'custom';
}

// ── Helpers ───────────────────────────────────────────────────
function Avatar({ name, size = 36, color = 'var(--primary)' }) {
  const initials = (name || '?').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: `linear-gradient(135deg, ${color} 0%, #6366f1 100%)`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', fontSize: size * 0.35, fontWeight: 800, flexShrink: 0,
    }}>{initials}</div>
  );
}

function Badge({ label, color, bg }) {
  return (
    <span style={{
      display: 'inline-block', background: bg, color,
      fontSize: 11, fontWeight: 800, padding: '3px 10px',
      borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.4px',
    }}>{label}</span>
  );
}

function Card({ children, style, ...rest }) {
  return (
    <div
      style={{
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        borderRadius: 14, ...style,
      }}
      {...rest}
    >{children}</div>
  );
}

function EmptyState({ icon, title, sub }) {
  return (
    <div style={{ textAlign: 'center', padding: '52px 24px', color: 'var(--text-muted)' }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-dark)', marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 13.5 }}>{sub}</div>
    </div>
  );
}

function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 24 }}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)} style={{
          padding: '12px 20px', border: 'none', background: 'none',
          cursor: 'pointer', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700,
          color: active === t.id ? 'var(--primary)' : 'var(--text-muted)',
          borderBottom: active === t.id ? '2px solid var(--primary)' : '2px solid transparent',
          marginBottom: -1, display: 'flex', alignItems: 'center', gap: 7,
          transition: 'color 0.15s',
        }}>
          {t.icon} {t.label}
          {t.count > 0 && (
            <span style={{
              background: t.id === 'pending' ? 'var(--warning)' : 'var(--primary)',
              color: '#fff', fontSize: 10, fontWeight: 800,
              borderRadius: 10, padding: '1px 6px', minWidth: 18, textAlign: 'center',
            }}>{t.count}</span>
          )}
        </button>
      ))}
    </div>
  );
}

// ── Permission picker ─────────────────────────────────────────
function PermissionPicker({ value, onChange }) {
  const preset = presetFromPerms(value);

  function selectPreset(p) {
    if (p.perms) onChange(p.perms);
  }

  function togglePerm(key) {
    if (value.includes(key)) onChange(value.filter(k => k !== key));
    else onChange([...value, key]);
  }

  return (
    <div>
      {/* Preset buttons */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
        {ACCESS_PRESETS.filter(p => p.id !== 'custom').map(p => (
          <button key={p.id} onClick={() => selectPreset(p)} style={{
            padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
            border: `2px solid ${preset === p.id ? p.color : 'var(--border)'}`,
            background: preset === p.id ? p.bg : 'var(--surface)',
            textAlign: 'left', fontFamily: 'inherit',
            transition: 'all 0.15s',
          }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: preset === p.id ? p.color : 'var(--text-dark)' }}>{p.label}</div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>{p.desc}</div>
          </button>
        ))}
      </div>

      {/* Individual toggles */}
      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
        Or pick specific permissions
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {ALL_PERMS.map(p => {
          const on = value.includes(p.key);
          return (
            <label key={p.key} style={{
              display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
              padding: '9px 12px', borderRadius: 8,
              background: on ? 'var(--primary-light)' : 'var(--surface)',
              border: `1px solid ${on ? 'rgba(0,44,237,0.2)' : 'var(--border)'}`,
              transition: 'all 0.12s',
            }}>
              <input type="checkbox" checked={on} onChange={() => togglePerm(p.key)} style={{ accentColor: 'var(--primary)', width: 15, height: 15 }} />
              <span style={{ fontSize: 16 }}>{p.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: on ? 'var(--primary)' : 'var(--text-dark)' }}>{p.label}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{p.desc}</div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

// ── Invite modal ──────────────────────────────────────────────
function InviteModal({ bots, onClose, onInvited }) {
  const [email, setEmail] = useState('');
  const [bId, setBId] = useState(bots[0]?.bId ?? '');
  const [perms, setPerms] = useState(['commands', 'panel', 'flows', 'users', 'logs']);
  const [loading, setLoading] = useState(false);

  async function submit() {
    if (!email.trim()) return toast.error('Enter the collaborator\'s email.');
    if (!bId) return toast.error('Select a bot.');
    if (!perms.length) return toast.error('Select at least one permission.');
    setLoading(true);
    try {
      const { data } = await inviteCollaborator({ email: email.trim(), bId, permissions: perms });
      if (data?.success) {
        toast.success('Invitation sent!');
        onInvited();
        onClose();
      } else {
        toast.error(data?.message || 'Failed to send invitation.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to send invitation.');
    }
    setLoading(false);
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(13,15,26,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 999, padding: 20,
    }} onClick={onClose}>
      <div style={{
        background: 'var(--bg-card)', borderRadius: 18, width: '100%', maxWidth: 520,
        maxHeight: '90vh', overflowY: 'auto', padding: '28px 28px',
        boxShadow: 'var(--shadow-lg)',
      }} onClick={e => e.stopPropagation()}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: 'var(--text-dark)' }}>Invite Collaborator</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>Send an invite to collaborate on one of your bots</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)', lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

          {/* Email */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              Collaborator's Email
            </label>
            <input
              style={{ width: '100%', padding: '11px 14px', fontSize: 13.5, border: '1.5px solid var(--border)', borderRadius: 10, background: 'var(--surface)', color: 'var(--text-dark)', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="name@example.com"
              type="email"
            />
          </div>

          {/* Bot selector */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              Which Bot
            </label>
            <select
              style={{ width: '100%', padding: '11px 14px', fontSize: 13.5, border: '1.5px solid var(--border)', borderRadius: 10, background: 'var(--surface)', color: 'var(--text-dark)', outline: 'none', fontFamily: 'inherit', cursor: 'pointer' }}
              value={bId}
              onChange={e => setBId(e.target.value)}>
              {bots.map(b => (
                <option key={b.bId} value={b.bId}>{b.bName}</option>
              ))}
            </select>
          </div>

          {/* Permissions */}
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              Access Level
            </label>
            <PermissionPicker value={perms} onChange={setPerms} />
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
            <button className="btn btn-primary" onClick={submit} disabled={loading} style={{ flex: 1 }}>
              {loading ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Sending…</> : '✉️ Send Invitation'}
            </button>
            <button className="btn btn-outline" onClick={onClose} disabled={loading}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Edit access modal ─────────────────────────────────────────
function EditAccessModal({ collab, onClose, onSaved }) {
  const [perms, setPerms] = useState(collab.permissions ?? []);
  const [loading, setLoading] = useState(false);

  async function save() {
    if (!perms.length) return toast.error('Select at least one permission.');
    setLoading(true);
    try {
      const { data } = await updateCollabAccess(collab.cId, perms);
      if (data?.success) { toast.success('Access updated.'); onSaved(); onClose(); }
      else toast.error(data?.message || 'Failed to update.');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update.');
    }
    setLoading(false);
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(13,15,26,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: 20 }} onClick={onClose}>
      <div style={{ background: 'var(--bg-card)', borderRadius: 18, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', padding: '28px', boxShadow: 'var(--shadow-lg)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 900, color: 'var(--text-dark)' }}>Edit Access</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>{collab.email}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: 'var(--text-muted)' }}>✕</button>
        </div>
        <PermissionPicker value={perms} onChange={setPerms} />
        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button className="btn btn-primary" onClick={save} disabled={loading} style={{ flex: 1 }}>
            {loading ? 'Saving…' : 'Save Changes'}
          </button>
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Collaborator row (my bots → outgoing) ─────────────────────
function CollabRow({ collab, onRemove, onEdit }) {
  const [removing, setRemoving] = useState(false);
  const preset = ACCESS_PRESETS.find(p => p.id === presetFromPerms(collab.permissions ?? []));
  const statusColors = {
    pending: { color: 'var(--warning)', bg: 'rgba(255,149,0,0.10)', label: 'Pending' },
    active: { color: 'var(--success)', bg: 'rgba(0,196,140,0.10)', label: 'Active' },
    declined: { color: 'var(--danger)', bg: 'rgba(255,59,48,0.08)', label: 'Declined' },
  };
  const sc = statusColors[collab.status] ?? statusColors.pending;

  async function handleRemove() {
    if (!window.confirm(`Remove ${collab.email} from this bot?`)) return;
    setRemoving(true);
    try {
      const { data } = await removeCollaborator(collab.cId);
      if (data?.success) { toast.success('Collaborator removed.'); onRemove(collab.cId); }
      else toast.error(data?.message || 'Failed to remove.');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to remove.');
    }
    setRemoving(false);
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
      <Avatar name={collab.email} size={38} color="#6366f1" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--text-dark)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {collab.name || collab.email}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {collab.email} · {collab.botName ?? collab.bId}
        </div>
      </div>
      <Badge label={preset?.label ?? 'Custom'} color={preset?.color ?? 'var(--text-muted)'} bg={preset?.bg ?? 'var(--surface)'} />
      <Badge label={sc.label} color={sc.color} bg={sc.bg} />
      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
        {collab.status !== 'declined' && (
          <button className="btn btn-outline" style={{ fontSize: 12, padding: '5px 12px' }} onClick={() => onEdit(collab)}>Edit</button>
        )}
        <button className="btn btn-outline" style={{ fontSize: 12, padding: '5px 12px', color: 'var(--danger)', borderColor: 'rgba(255,59,48,0.3)' }} onClick={handleRemove} disabled={removing}>
          {removing ? '…' : 'Remove'}
        </button>
      </div>
    </div>
  );
}

// ── Invite card (received — pending) ─────────────────────────
function InviteCard({ invite, onRespond }) {
  const [loading, setLoading] = useState(false);
  const preset = ACCESS_PRESETS.find(p => p.id === presetFromPerms(invite.permissions ?? []));

  async function respond(accept) {
    setLoading(true);
    try {
      const { data } = await respondToInvite(invite.cId, accept);
      if (data?.success) {
        toast.success(accept ? '✅ Invitation accepted!' : 'Invitation declined.');
        onRespond(invite.cId);
      } else {
        toast.error(data?.message || 'Failed to respond.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to respond.');
    }
    setLoading(false);
  }

  return (
    <Card style={{ padding: '20px 22px', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>
          🤖
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 14.5, color: 'var(--text-dark)', marginBottom: 3 }}>
            {invite.botName ?? `Bot #${invite.bId}`}
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
            Invited by <strong style={{ color: 'var(--text-dark)' }}>{invite.ownerName || invite.ownerEmail}</strong>
            {invite.invitedAt && <> · {new Date(invite.invitedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</>}
          </div>

          {/* Permissions granted */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 8 }}>
              Access granted
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <Badge
                label={preset?.label ?? 'Custom'}
                color={preset?.color ?? 'var(--warning)'}
                bg={preset?.bg ?? 'rgba(255,149,0,0.08)'}
              />
              {(invite.permissions ?? []).map(p => {
                const def = ALL_PERMS.find(a => a.key === p);
                return def ? (
                  <span key={p} style={{ fontSize: 12, background: 'var(--surface)', color: 'var(--text-mid)', border: '1px solid var(--border)', borderRadius: 20, padding: '2px 10px', fontWeight: 600 }}>
                    {def.icon} {def.label}
                  </span>
                ) : null;
              })}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary" style={{ fontSize: 13, padding: '8px 20px' }} onClick={() => respond(true)} disabled={loading}>
              {loading ? '…' : '✓ Accept'}
            </button>
            <button className="btn btn-outline" style={{ fontSize: 13, padding: '8px 16px', color: 'var(--danger)', borderColor: 'rgba(255,59,48,0.25)' }} onClick={() => respond(false)} disabled={loading}>
              Decline
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ── Shared bot card (bots shared with me — active) ────────────
function SharedBotCard({ bot, navigate }) {
  const preset = ACCESS_PRESETS.find(p => p.id === presetFromPerms(bot.permissions ?? []));

  return (
    <Card
      style={{ padding: '16px 18px', cursor: 'pointer', transition: 'box-shadow 0.15s' }}
      onClick={() => navigate(`/bots/${bot.bId}`)}
      onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow-md)'}
      onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, flexShrink: 0,
          background: 'linear-gradient(135deg, #6366f1 0%, var(--primary) 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 800, fontSize: 16,
        }}>
          {bot.bName?.[0]?.toUpperCase() ?? '?'}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              fontWeight: 800, fontSize: 14.5, color: 'var(--text-dark)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0,
            }}>
              {bot.bName}
            </div>
            <span style={{ fontSize: 16, color: 'var(--text-muted)', flexShrink: 0 }}>→</span>
          </div>

          <div style={{
            fontSize: 12, color: 'var(--text-muted)', marginTop: 2,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            Owner: {bot.ownerName || bot.ownerEmail}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            <Badge label={preset?.label ?? 'Custom'} color={preset?.color ?? 'var(--warning)'} bg={preset?.bg ?? 'rgba(255,149,0,0.08)'} />
            {(bot.permissions ?? []).map(p => {
              const def = ALL_PERMS.find(a => a.key === p);
              return def ? (
                <span key={p} title={def.label} style={{
                  fontSize: 12, color: 'var(--text-muted)', background: 'rgba(0,0,0,0.04)',
                  borderRadius: 6, padding: '2px 7px', lineHeight: '16px',
                }}>
                  {def.icon}
                </span>
              ) : null;
            })}
          </div>
        </div>
      </div>
    </Card>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function CollaborationsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedTab = searchParams.get('tab'); // deep link from invite email, e.g. ?tab=pending

  const [tab, setTab] = useState(
    ['shared', 'sent', 'pending'].includes(requestedTab) ? requestedTab : 'shared'
  ); // shared | sent | pending
  const [myBots, setMyBots] = useState([]);
  const [sentInvites, setSentInvites] = useState([]);          // invites I sent
  const [pending, setPending] = useState([]);          // invites received (pending)
  const [sharedBots, setSharedBots] = useState([]);          // bots shared WITH me (active)
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [editingCollab, setEditingCollab] = useState(null);
  const [canCollab, setCanCollab] = useState(true);        // plan gate

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sentRes, receivedRes, sharedRes, botsRes, billingRes] = await Promise.allSettled([
        listCollabs(),
        listReceivedInvites(),
        listCollabBots(),
        listBots(),
        getBillingOverview(),
      ]);

      if (sentRes.status === 'fulfilled') setSentInvites(sentRes.value?.data?.collabs ?? []);
      if (receivedRes.status === 'fulfilled') {
        const invites = receivedRes.value?.data?.invites ?? [];
        setPending(invites);
        // Safety net: if no tab was explicitly requested (e.g. an old email link)
        // and there's a pending invite waiting, don't leave the user stuck on an
        // empty "Shared with me" tab — surface the invite automatically.
        if (!requestedTab && invites.some(i => i.status === 'pending')) {
          setTab('pending');
        }
      }
      if (sharedRes.status === 'fulfilled') setSharedBots(sharedRes.value?.data?.bots ?? []);
      if (botsRes.status === 'fulfilled') setMyBots(botsRes.value?.data?.bots ?? []);
      if (billingRes.status === 'fulfilled') {
        const plan = billingRes.value?.data?.current_plan;
        setCanCollab(plan?.plAllowCollaboration !== false ? !!plan?.plAllowCollaboration : false);
      }
    } catch { /* handled per-call */ }
    setLoading(false);
  }, [requestedTab]);


  useEffect(() => { load(); }, [load]);

  const pendingCount = pending.filter(i => i.status === 'pending').length;

  const tabs = [
    { id: 'shared', icon: '🤝', label: 'Shared with me', count: sharedBots.length },
    { id: 'pending', icon: '📬', label: 'Invitations', count: pendingCount },
    { id: 'sent', icon: '📤', label: 'My Collaborators', count: 0 },
  ];

  return (
    <div style={{ padding: '36px 40px', maxWidth: 860, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 900, color: 'var(--text-dark)' }}>Collaborations</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13.5, color: 'var(--text-muted)' }}>
            Invite people to help manage your bots, or access bots others have shared with you
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => {
            if (!canCollab) { navigate('/billing'); return; }
            setShowInvite(true);
          }}
          disabled={myBots.length === 0 && canCollab}
          title={!canCollab ? 'Upgrade your plan to invite collaborators' : myBots.length === 0 ? 'Create a bot first' : ''}>
          {!canCollab ? '🔒 Upgrade to Invite' : '+ Invite Collaborator'}
        </button>
      </div>

      {!canCollab && (
        <div style={{
          marginBottom: 20, padding: '12px 16px', borderRadius: 'var(--radius)',
          background: 'rgba(255,149,0,0.08)', border: '1.5px solid rgba(255,149,0,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ fontSize: 13, color: 'var(--text-dark)' }}>
            🔒 <strong>Collaborations</strong> are not included in your current plan.
            You can still accept invitations from others, but you cannot send new invites.
          </div>
          <button className="btn btn-primary" style={{ fontSize: 12, padding: '6px 14px', flexShrink: 0 }}
            onClick={() => navigate('/billing')}>
            Upgrade
          </button>
        </div>
      )}

      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
          <span className="spinner spinner-dark" /> Loading…
        </div>
      ) : (

        <>
          {/* ── Tab: Shared with me ── */}
          {tab === 'shared' && (
            sharedBots.length === 0
              ? <EmptyState icon="🤝" title="No shared bots yet" sub="When someone invites you to collaborate on their bot and you accept, it will appear here." />
              : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>
                    {sharedBots.length} bot{sharedBots.length !== 1 ? 's' : ''} shared with you — click to open
                  </div>
                  {sharedBots.map(b => (
                    <SharedBotCard key={b.bId + b.cId} bot={b} navigate={navigate} />
                  ))}
                </div>
              )
          )}

          {/* ── Tab: Invitations received ── */}
          {tab === 'pending' && (
            pending.length === 0
              ? <EmptyState icon="📬" title="No invitations" sub="When someone invites you to collaborate on their bot, you'll see it here." />
              : (
                <div>
                  {pending.filter(i => i.status === 'pending').length > 0 && (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>
                        Pending ({pending.filter(i => i.status === 'pending').length})
                      </div>
                      {pending.filter(i => i.status === 'pending').map(inv => (
                        <InviteCard key={inv.cId} invite={inv} onRespond={() => load()} />
                      ))}
                    </>
                  )}
                  {pending.filter(i => i.status !== 'pending').length > 0 && (
                    <>
                      <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 28, marginBottom: 12 }}>
                        Past
                      </div>
                      {pending.filter(i => i.status !== 'pending').map(inv => (
                        <Card key={inv.cId} style={{ padding: '14px 18px', marginBottom: 10, opacity: 0.6 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <span style={{ fontSize: 20 }}>🤖</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{inv.botName ?? `Bot #${inv.bId}`}</div>
                              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>From {inv.ownerName || inv.ownerEmail}</div>
                            </div>
                            <Badge
                              label={inv.status === 'declined' ? 'Declined' : 'Inactive'}
                              color="var(--text-muted)"
                              bg="rgba(154,160,192,0.12)"
                            />
                          </div>
                        </Card>
                      ))}
                    </>
                  )}
                </div>
              )
          )}

          {/* ── Tab: My collaborators (sent) ── */}
          {tab === 'sent' && (
            sentInvites.length === 0
              ? (
                <EmptyState
                  icon="📤"
                  title="No collaborators yet"
                  sub="Invite someone to help manage one of your bots. You control exactly what they can access."
                />
              ) : (
                <Card>
                  {/* Group by bot */}
                  {Object.entries(
                    sentInvites.reduce((acc, c) => {
                      const key = c.bId;
                      if (!acc[key]) acc[key] = { botName: c.botName ?? c.bId, items: [] };
                      acc[key].items.push(c);
                      return acc;
                    }, {})
                  ).map(([bId, group]) => (
                    <div key={bId}>
                      <div style={{ padding: '12px 20px 6px', fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid var(--border)' }}>
                        🤖 {group.botName}
                      </div>
                      {group.items.map(c => (
                        <CollabRow
                          key={c.cId}
                          collab={c}
                          onRemove={cId => setSentInvites(prev => prev.filter(x => x.cId !== cId))}
                          onEdit={c => setEditingCollab(c)}
                        />
                      ))}
                    </div>
                  ))}
                </Card>
              )
          )}
        </>
      )}

      {/* Modals */}
      {showInvite && (
        <InviteModal
          bots={myBots}
          onClose={() => setShowInvite(false)}
          onInvited={load}
        />
      )}

      {editingCollab && (
        <EditAccessModal
          collab={editingCollab}
          onClose={() => setEditingCollab(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}